import { getEnv } from "../config/env";

// services/PackageModalApiService.js - Separate API service for Create Package Modal only

class PackageModalApiService {
	constructor() {
		const env = getEnv();
		this.proxyUrl = env.REACT_APP_PROXY;
		this.timeout = 300000;

		console.log(
			"📦 PackageModalApiService loaded - separate from package search flow",
		);
	}

	setProxyUrl(url) {
		this.proxyUrl = url.replace(/\/$/, "");
	}

	getSharedAuthToken() {
		if (
			window.apiService &&
			window.apiService.tokens &&
			window.apiService.tokens.idToken
		) {
			return window.apiService.tokens.idToken;
		}
		return (
			localStorage.getItem("google_id_token") ||
			localStorage.getItem("authToken")
		);
	}

	// Authentication check
	isAuthenticated() {
		if (
			window.apiService &&
			typeof window.apiService.isAuthenticated === "function"
		) {
			try {
				return window.apiService.isAuthenticated();
			} catch (error) {
				console.warn("Main ApiService auth check failed:", error);
				return false;
			}
		}

		const token = this.getSharedAuthToken();
		if (!token) {
			return false;
		}

		return this.checkTokenExpiration(token);
	}

	checkTokenExpiration(token) {
		if (!token) return false;

		try {
			const payload = this.parseJWT(token);
			if (!payload || !payload.exp) return false;

			const now = Date.now();
			const expiration = payload.exp * 1000;
			return expiration > now;
		} catch (error) {
			console.warn("Token validation failed:", error);
			return false;
		}
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		const token = this.getSharedAuthToken();
		if (token && this.checkTokenExpiration(token)) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	async makeRequest(endpoint, options = {}) {
		try {
			const token = this.getSharedAuthToken();
			if (!token) {
				return {
					success: false,
					error: "Authentication required",
					status: 401,
				};
			}

			const headers = this.getHeaders();
			const normalizedEndpoint = endpoint.startsWith("/")
				? endpoint
				: `/${endpoint}`;
			const url = `${this.proxyUrl}${normalizedEndpoint}`;

			const requestOptions = {
				method: "GET",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			console.log(`📦 Modal API Request: ${requestOptions.method} ${url}`);

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			console.log(
				`📦 Modal API Response: ${response.status} ${response.statusText}`,
			);

			if (response.status === 401 || response.status === 403) {
				return {
					success: false,
					error: "Authentication failed",
					status: response.status,
				};
			}

			if (!response.ok) {
				const errorText = await response.text().catch(() => "Unknown error");
				console.error("Modal API Error Response:", errorText);
				throw new Error(
					`HTTP ${response.status}: ${response.statusText}. ${errorText}`,
				);
			}

			const data = await response.json();
			console.log("📦 Modal API Response received successfully");

			return {
				success: true,
				data: data,
				status: response.status,
			};
		} catch (error) {
			console.error("📦 Modal API Request Error:", error);

			let errorMessage = error.message;
			if (
				error.name === "TypeError" &&
				error.message.includes("Failed to fetch")
			) {
				errorMessage = "Network error: Unable to connect to API";
			}

			return {
				success: false,
				error: errorMessage,
				status: error.status || 500,
			};
		}
	}

	// Search audiences specifically for package modal
	async searchModalAudiences(searchParams = {}) {
		const requestId = Date.now();
		try {
			console.log(
				`👥 [${requestId}] Modal: Searching audiences with params:`,
				searchParams,
			);

			const {
				searchQuery = "",
				countries = [],
				deviceTypes = [],
				categories = [],
				distributionChannels = [],
				appBundleIds = [],
				publisherIds = [],
				languages = [],
				adPlacements = [],
				signal,
			} = searchParams;

			// Build query array from search term
			const queryArray = searchQuery.trim()
				? [
						{
							query: searchQuery.trim(),
							threshold: 0.75,
						},
					]
				: [];

			// Build the audience search payload for modal
			const audiencePayload = {
				query: queryArray,
				excludeQuery: [],
				filters: {
					audienceVector: {
						// All basic fields set to null as per requirement
						accountId: null,
						accountName: null,
						description: null,
						audienceName: null,
						type: null,
						dataSource: null,
						region: null,
						estimatedReach: 0,
						definition: null,
						jobId: null,
						reach: 0,
						bqTable: null,
						addressableReach: 0,
						isArchived: false,
						bqReachTable: null,
						normalizedDefinition: null,
						reportStatus: null,
						info: null,
						error: null,
						tags: null,
						directAudienceProvider: null,
						exportType: null,
						userId: null,
						exportTargets: null,
						lastUdsTimestamp: null,
						errorCode: null,
						lastExportId: null,
						createdWith: null,
						shareableOpenxAudience: false,
						pricing: null,
						createdAt: null,
						cleanAudienceName: null,
						primaryPersona: null,
						audienceStrategy: null,
						technicalAttributes: null,
						status: null,
						notesDescription: null,
						hasMeta: false,
						entity: null,
						event: null,
					},
					audienceMetrics: {
						// All metrics set to 0/null as per requirement
						reportMonth: null,
						audienceId: null,
						status: null,
						reach: 0,
						requests: 0,
						soldImpressions: 0,
						uniqueUsers: 0,
						uniqueUsersWithRequests: 0,
						uniqueUsersWithImpressions: 0,
						cookies: 0,
						cookiesWithRequests: 0,
						cookiesWithImpressions: 0,
						desktopsWithRequests: 0,
						mobileDevices: 0,
						mobileDevicesWithRequests: 0,
						mobileDevicesWithImpressions: 0,
						tabletDevices: 0,
						tabletDevicesWithRequests: 0,
						tabletDevicesWithImpressions: 0,
						connectedTvDevices: 0,
						connectedTvDevicesWithRequests: 0,
						connectedTvDevicesWithImpressions: 0,
						displayRequests: 0,
						videoRequests: 0,
						mobileappRequests: 0,
						mobilewebRequests: 0,
						desktopRequests: 0,
						tabletRequests: 0,
						households: 0,
						householdsWithRequests: 0,
						householdsWithImpressions: 0,
						idlXyWithRequests: 0,
						idlXiWithRequests: 0,
						idlXcWithRequests: 0,
						idlXmWithRequests: 0,
						idlXyWithImpressions: 0,
						idlXiWithImpressions: 0,
						idlXcWithImpressions: 0,
						idlXmWithImpressions: 0,
						id5s: 0,
						id5WithRequests: 0,
						id5WithImpressions: 0,
						connectedTvDeviceIdRequests: 0,
						connectedTvDeviceIdSoldImpressions: 0,
						connectedTvDeviceIdDevices: 0,
						connectedTvDeviceIdSoldImpressionsWithRequests: 0,
						connectedTvDeviceIdSoldImpressionsWithImpressions: 0,
						uniqueUsersViaConnectedTv: 0,
						uniqueUsersViaConnectedTvWithRequests: 0,
						uniqueUsersViaConnectedTvWithImpressions: 0,
						idlXyViaConnectedTv: 0,
						idlXyViaConnectedTvWithRequests: 0,
						idlXyViaConnectedTvWithImpressions: 0,
						idlXiViaConnectedTv: 0,
						idlXiViaConnectedTvWithRequests: 0,
						idlXiViaConnectedTvWithImpressions: 0,
						uniqueIpsWithRequests: 0,
						ipRequests: 0,
						advertiserSpend: 0,
						avgSpendPerUniqueUser: 0,
						avgEcpm: 0,
						stddevEcpm: 0,
						minEcpm: 0,
						maxEcpm: 0,
						impressionFrequency: 0,
						impressionFrequencyPerDay: 0,
						connectedTvDevices: 0,
						avgSpendPerUniqueConnectedTvDeviceId: 0,
						connectedTvDeviceIdAvgEcpm: 0,
						connectedTvDeviceIdStddevEcpm: 0,
						connectedTvDeviceIdMinEcpm: 0,
						connectedTvDeviceIdMaxEcpm: 0,
						connectedTvDeviceIdImpressionFrequency: 0,
						connectedTvDeviceIdImpressionFrequencyPerDay: 0,
						clickThroughRate: 0,
						revenuePerImpression: 0,
						spendPerImpression: 0,
						vtr: 0,
						viewability: 0,
						oxWinRate: 0,
						pubWinRate: 0,
						bidCpm: 0,
						bidGuidance: 0,
						dealIds: null,
						dealNks: null,
						cprStartFirstOccurrence: 0,
						cprMarketDemandPartnerSpend: 0,
						cprMarketPublisherRevenueInUSD: 0,
						completeFirstOccurrence: 0,
						fills: 0,
						nonZeroBids: 0,
						impressions: 0,
						dealVolumeDealInfo: 0,
						nonZeroBidsValueInUSD: 0,
						bidSpendInUSD: 0,
						startFirstOccurrence: 0,
						viewables: null,
						viewabilityMeasures: null,
					},
					cumulativeDataFilters: {
						cumulativeSetsFiltersMap: {
							Relevance: {
								sortBy: "compositeScore",
								isASC: false,
								filterBy: "reach",
								threshold: 1000,
							},
							Performance: {
								sortBy: "avgEcpm",
								isASC: false,
								filterBy: "avgEcpm",
								threshold: 5000.5,
							},
							CostOptimized: {
								sortBy: "reach",
								isASC: true,
								filterBy: "reach",
								threshold: 1000,
							},
						},
					},
				},
				sortBy: "compositeScore",
				view: "DESC",
				startIndex: 0,
				noOfResult: 100,
				noOfDays: 30,
				isSegmentsRequire: false,
			};

			console.log(`👥 [${requestId}] Modal audience search payload prepared`);

			const response = await this.makeRequest("/audience/search", {
				method: "POST",
				body: JSON.stringify(audiencePayload),
				signal,
			});

			if (response.success) {
				console.log(
					`✅ [${requestId}] Modal audience search completed successfully`,
				);
				return response;
			} else {
				console.error(
					`❌ [${requestId}] Modal audience search failed:`,
					response.error,
				);
				return response;
			}
		} catch (error) {
			console.error(`❌ [${requestId}] Modal searchAudiences failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	// Search domains specifically for package modal
	async searchModalDomains(searchParams = {}) {
		const requestId = Date.now();
		try {
			console.log(
				`🌐 [${requestId}] Modal: Searching domains with params:`,
				searchParams,
			);

			const {
				searchQuery = "",
				countries = [],
				deviceTypes = [],
				categories = [],
				distributionChannels = [],
				appBundleIds = [],
				publisherIds = [],
				languages = [],
				adPlacements = [],
				signal,
			} = searchParams;

			// Build query array from search term
			const queryArray = searchQuery.trim()
				? [
						{
							query: searchQuery.trim(),
							threshold: 0.75,
						},
					]
				: [];

			// Map targeting parameters to domain filters
			const geography =
				countries.length > 0
					? countries.map((country) => {
							const countryMap = {
								"United States": "US",
								Canada: "CA",
								"United Kingdom": "UK",
								Germany: "DE",
								France: "FR",
								Australia: "AU",
								Japan: "JP",
								India: "IN",
								Brazil: "BR",
								Spain: "ES",
								Italy: "IT",
								Netherlands: "NL",
								Mexico: "MX",
								"South Korea": "KR",
							};
							return countryMap[country] || country;
						})
					: ["US"];

			const mappedDeviceTypes =
				deviceTypes.length > 0
					? deviceTypes.map((device) => {
							const deviceMap = {
								Mobile: "phone",
								Tablet: "tablet",
								Desktop: "desktop",
								"Connected TV": "mobile/tablet",
								"Smart TV": "mobile/tablet",
							};
							return deviceMap[device] || device.toLowerCase();
						})
					: ["phone", "tablet", "desktop", "mobile/tablet"];

			const mappedChannels =
				distributionChannels.length > 0
					? distributionChannels.map((channel) => {
							const channelMap = {
								Web: "Site",
								"Mobile App": "App",
								"In-App": "App",
							};
							return channelMap[channel] || "Site";
						})
					: ["Site"];

			// Build the domain search payload for modal
			const domainPayload = {
				query: queryArray,
				excludeQuery: [],
				filters: {
					performance: {
						minViewability: 0,
						minReach: 0,
						performanceData: [
							{
								filterColumn: "cprmarketrequests",
								filterThreshold: 10000,
							},
							{
								filterColumn: "impressions",
								filterThreshold: 100,
							},
						],
					},
					domainFilters: {
						geography: geography,
						contentStyle: [],
						videoplcmt: [],
						distributionChannel: mappedChannels,
						deviceTypes: mappedDeviceTypes,
						mediaSubtype: ["Display"],
						publisher: [],
						isDirect: null,
					},
					vectorFilters: {
						safetyTier: [],
						content_style: [],
						iab_category: [],
						tier_Details: {
							tier: 0,
							keys: [],
						},
						categoryThreshold: {
							iab_category_score: 0.75,
							safety_tier_cscore: 0,
							content_style_cscore: 0,
						},
					},
					noOfResult: 100,
					noOfDays: 30,
					cumulativeDataFilters: {
						perfomanceData: "avg_viewability",
						reachThreashold: 0,
						marketThreashold: 100000000,
					},
					includeDomains: [],
					excludeDomains: [],
				},
				sortBy: "totalImpressions",
				view: "DESC",
			};

			console.log(`🌐 [${requestId}] Modal domain search payload prepared`);

			const response = await this.makeRequest("/domains/search", {
				method: "POST",
				body: JSON.stringify(domainPayload),
				signal,
			});

			if (response.success) {
				console.log(
					`✅ [${requestId}] Modal domain search completed successfully`,
				);
				return response;
			} else {
				console.error(
					`❌ [${requestId}] Modal domain search failed:`,
					response.error,
				);
				return response;
			}
		} catch (error) {
			console.error(`❌ [${requestId}] Modal searchDomains failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}

	// Combined search function specifically for package modal
	async searchPackageModalResults(searchParams = {}) {
		const requestId = Date.now();
		try {
			console.log(`🔍 [${requestId}] Modal: Starting combined package search`);

			// Run both searches in parallel
			const [audienceResponse, domainResponse] = await Promise.allSettled([
				this.searchModalAudiences(searchParams),
				this.searchModalDomains(searchParams),
			]);

			// Process audience results
			const audienceResults =
				audienceResponse.status === "fulfilled" &&
				audienceResponse.value.success
					? audienceResponse.value.data
					: [];

			// Process domain results
			const domainResults =
				domainResponse.status === "fulfilled" && domainResponse.value.success
					? domainResponse.value.data
					: [];

			console.log(`✅ [${requestId}] Modal combined search completed:`, {
				audienceCount: Array.isArray(audienceResults)
					? audienceResults.length
					: 0,
				domainCount: Array.isArray(domainResults) ? domainResults.length : 0,
				audienceSuccess:
					audienceResponse.status === "fulfilled" &&
					audienceResponse.value.success,
				domainSuccess:
					domainResponse.status === "fulfilled" && domainResponse.value.success,
			});

			return {
				success: true,
				data: {
					audiences: audienceResults,
					domains: domainResults,
					audienceResponse:
						audienceResponse.status === "fulfilled"
							? audienceResponse.value
							: null,
					domainResponse:
						domainResponse.status === "fulfilled" ? domainResponse.value : null,
				},
			};
		} catch (error) {
			console.error(`❌ [${requestId}] Modal combined search failed:`, error);
			return {
				success: false,
				error: error.message,
				data: null,
			};
		}
	}
}

// Create and export the modal-specific service instance
const packageModalApiService = new PackageModalApiService();
window.packageModalApiService = packageModalApiService;

console.log(
	"📦 PackageModalApiService loaded - separate from main package flow",
);

export default packageModalApiService;
